import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Globe, Search, CheckCircle, XCircle, Loader, ExternalLink } from 'lucide-react';
import './WebsiteScanner.css';

const WebsiteScanner = ({ webpages, setWebpages }) => {
  const { user } = useAuth();
  const [url, setUrl] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanId, setScanId] = useState(null);
  const [scanStatus, setScanStatus] = useState(null); // eslint-disable-line no-unused-vars
  const [pages, setPages] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoadingWebpages, setIsLoadingWebpages] = useState(false);

  // Load existing webpages on component mount
  useEffect(() => {
    loadWebpages();
    checkForInProgressScans();
    checkForProcessingState();
  }, [user?.agent?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkForInProgressScans = async () => {
    if (!user?.agent?.id) return;
    
    try {
      const response = await fetch(`/api/website-scan/agent/${user.agent.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const scans = await response.json();
        const inProgressScan = scans.find(scan => scan.status === 'in_progress');
        
        if (inProgressScan) {
          setScanId(inProgressScan.id);
          setIsScanning(true);
          setMessage('Resuming previous scan...');
        }
      }
    } catch (error) {
      console.error('Error checking for in-progress scans:', error);
    }
  };

  const checkForProcessingState = async () => {
    if (!user?.agent?.id) return;
    
    try {
      // Check localStorage for processing state
      const processingState = localStorage.getItem('websiteProcessing');
      if (processingState) {
        const state = JSON.parse(processingState);
        const timeElapsed = Date.now() - state.startTime;
        const maxProcessingTime = state.estimatedMinutes * 60 * 1000; // Convert to milliseconds
        
        // If processing has been going on too long, clear it
        if (timeElapsed > maxProcessingTime + 60000) { // Add 1 minute buffer
          localStorage.removeItem('websiteProcessing');
          return;
        }
        
        // Restore processing state
        setIsProcessing(true);
        setScanId(state.scanId);
        setSelectedPages(state.selectedPages);
        setMessage(`Processing ${state.selectedPages.length} pages... Estimated time: ${state.estimatedMinutes} minute${state.estimatedMinutes !== 1 ? 's' : ''}`);
        
        // Load the pages for this scan
        const pagesResponse = await fetch(`/api/website-scan/scan/${state.scanId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (pagesResponse.ok) {
          const data = await pagesResponse.json();
          if (data.pages && data.pages.length > 0) {
            setPages(data.pages);
          }
        }
      }
    } catch (error) {
      console.error('Error checking for processing state:', error);
      // Clear invalid state
      localStorage.removeItem('websiteProcessing');
    }
  };

  const loadWebpages = async () => {
    if (!user?.agent?.id) return;
    
    setIsLoadingWebpages(true);
    try {
      const response = await fetch(`/api/website-scan/agent/${user.agent.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const scans = await response.json();
        // Get all pages from all scans
        const allPages = [];
        for (const scan of scans) {
          const pagesResponse = await fetch(`/api/website-scan/scan/${scan.id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          if (pagesResponse.ok) {
            const data = await pagesResponse.json();
            allPages.push(...data.pages.map(page => ({
              ...page,
              scanId: scan.id,
              scanDate: scan.created_at
            })));
          }
        }
        // Update parent state if callback provided
        if (setWebpages) {
          setWebpages(allPages);
        }
      }
    } catch (error) {
      console.error('Error loading webpages:', error);
    } finally {
      setIsLoadingWebpages(false);
    }
  };

  // Poll for scan status when scanning
  useEffect(() => {
    if (scanId && isScanning) {
      const interval = setInterval(async () => {
        try {
          const response = await fetch(`/api/website-scan/scan/${scanId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setScanStatus(data.scan);
            setPages(data.pages);
            
            if (data.scan.status === 'completed' || data.scan.status === 'failed') {
              setIsScanning(false);
              clearInterval(interval);
              
              if (data.scan.status === 'completed') {
                // Select all successful pages by default
                const successfulPages = data.pages.filter(page => page.success);
                setSelectedPages(successfulPages.map(page => page.id));
                setMessage(`Scan completed! Found ${successfulPages.length} pages.`);
              } else {
                setMessage('Scan failed. Please try again.');
              }
            }
          }
        } catch (error) {
          console.error('Error checking scan status:', error);
        }
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [scanId, isScanning]);

  const handleScan = async (e) => {
    e.preventDefault();
    
    if (!url.trim()) {
      setMessage('Please enter a URL');
      return;
    }

    if (!user?.agent?.id) {
      setMessage('No agent found. Please try logging in again.');
      return;
    }

    // Normalize URL - add http:// if no protocol specified
    let normalizedUrl = url.trim();
    if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
      normalizedUrl = 'https://' + normalizedUrl;
    }

    setIsScanning(true);
    setMessage('Starting website scan...');
    setPages([]);
    setSelectedPages([]);

    try {
      const response = await fetch('/api/website-scan/scan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          url: normalizedUrl,
          agentId: user.agent.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        setScanId(data.scanId);
        setMessage('Scan started. This may take a few minutes...');
      } else {
        const error = await response.json();
        setMessage(`Error: ${error.error}`);
        setIsScanning(false);
      }
    } catch (error) {
      console.error('Error starting scan:', error);
      setMessage('Error starting scan. Please try again.');
      setIsScanning(false);
    }
  };

  const handlePageToggle = (pageId) => {
    setSelectedPages(prev => 
      prev.includes(pageId) 
        ? prev.filter(id => id !== pageId)
        : [...prev, pageId]
    );
  };

  const handleSelectAll = () => {
    const successfulPages = pages.filter(page => page.success);
    setSelectedPages(successfulPages.map(page => page.id));
  };

  const handleSelectNone = () => {
    setSelectedPages([]);
  };

  const handleProcess = async () => {
    if (selectedPages.length === 0) {
      setMessage('Please select at least one page to process.');
      return;
    }

    // Calculate estimated processing time based on total words
    const totalWords = pages
      .filter(page => selectedPages.includes(page.id))
      .reduce((sum, page) => sum + page.wordCount, 0);
    
    // Rough estimate: 1-2 seconds per 100 words for facet extraction
    const estimatedSeconds = Math.max(10, Math.ceil(totalWords / 100 * 1.5));
    const estimatedMinutes = Math.ceil(estimatedSeconds / 60);

    setIsProcessing(true);
    setMessage(`Processing ${selectedPages.length} pages (${totalWords} words)... Estimated time: ${estimatedMinutes} minute${estimatedMinutes !== 1 ? 's' : ''}`);
    
    // Store processing state in localStorage
    localStorage.setItem('websiteProcessing', JSON.stringify({
      isProcessing: true,
      scanId: scanId,
      selectedPages: selectedPages,
      startTime: Date.now(),
      estimatedMinutes: estimatedMinutes
    }));

    try {
      const response = await fetch('/api/website-scan/process', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          scanId,
          selectedPageIds: selectedPages,
          agentId: user.agent.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessage(`Success! Processed ${data.pagesProcessed} pages (${data.totalWords} words) and added to your agent's knowledge base.`);
        setSelectedPages([]);
        setPages([]);
        setScanId(null);
        setScanStatus(null);
        loadWebpages(); // Reload the webpage list
        
        // Clear processing state from localStorage
        localStorage.removeItem('websiteProcessing');
      } else {
        const error = await response.json();
        setMessage(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error processing pages:', error);
      setMessage('Error processing pages. Please try again.');
      // Clear processing state from localStorage on error
      localStorage.removeItem('websiteProcessing');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveWebpage = async (pageId, scanId) => {
    try {
      // For now, we'll delete the entire scan since pages are processed together
      // TODO: Implement individual page removal when we separate processing
      const response = await fetch(`/api/website-scan/scan/${scanId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          agentId: user.agent.id
        })
      });

      if (response.ok) {
        setMessage('Webpage removed from knowledge base');
        loadWebpages(); // Reload the list
      } else {
        setMessage('Error removing webpage');
      }
    } catch (error) {
      console.error('Error removing webpage:', error);
      setMessage('Error removing webpage');
    }
  };

  const formatUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname + urlObj.pathname;
    } catch {
      return url;
    }
  };

  return (
    <div className="website-scanner" data-theme="dark">
      <div className="scanner-header">
        <Globe className="scanner-icon" />
        <h2>Scan Websites</h2>
        <p>Enter a website URL to scan and extract content for your agent's knowledge base.</p>
      </div>

      <form onSubmit={handleScan} className="scan-form">
        <div className="url-input-group">
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="example.com or https://example.com"
            disabled={isScanning}
            className="url-input"
          />
          <button 
            type="submit" 
            disabled={isScanning || !url.trim()}
            className="scan-button"
          >
            {isScanning ? (
              <>
                <Loader className="spinner" />
                Scanning...
              </>
            ) : (
              <>
                <Search />
                Scan Website
              </>
            )}
          </button>
        </div>
      </form>

      {message && (
        <div className={`message ${message.includes('Error') || message.includes('failed') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {/* Webpage Management Section */}
      {webpages.length > 0 && (
        <div className="webpage-management">
          <h3>Webpages in Knowledge Base ({webpages.length})</h3>
          <div className="webpage-list">
            {webpages.map((webpage) => (
              <div key={`${webpage.scanId}-${webpage.id}`} className="webpage-item">
                <div className="webpage-info">
                  <div className="webpage-title">
                    <Globe className="webpage-icon" />
                    <span>{webpage.title || 'Untitled'}</span>
                  </div>
                  <div className="webpage-details">
                    <span className="webpage-url">{formatUrl(webpage.url)}</span>
                    <span className="webpage-words">{webpage.wordCount} words</span>
                    <span className="webpage-date">
                      {new Date(webpage.scanDate).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveWebpage(webpage.id, webpage.scanId)}
                  className="remove-button"
                  title="Remove from knowledge base"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoadingWebpages && (
        <div className="loading-webpages">
          <Loader className="spinner" />
          <p>Loading webpages...</p>
        </div>
      )}

      {isScanning && (
        <div className="scanning-status">
          <Loader className="spinner" />
          <p>Scanning website... This may take a few minutes.</p>
          <p>Found {pages.length} pages so far...</p>
        </div>
      )}

      {pages.length > 0 && !isScanning && (
        <div className="pages-section">
          <div className="pages-header">
            <h3>Found {pages.length} pages</h3>
            <div className="selection-controls">
              <button onClick={handleSelectAll} className="select-button">
                Select All
              </button>
              <button onClick={handleSelectNone} className="select-button">
                Select None
              </button>
            </div>
          </div>

          <div className="pages-list">
            {pages.map((page) => (
              <div key={page.id} className={`page-item ${!page.success ? 'error' : ''}`}>
                <div className="page-checkbox">
                  <input
                    type="checkbox"
                    id={`page-${page.id}`}
                    checked={selectedPages.includes(page.id)}
                    onChange={() => handlePageToggle(page.id)}
                    disabled={!page.success}
                  />
                  <label htmlFor={`page-${page.id}`}></label>
                </div>
                
                <div className="page-content">
                  <div className="page-title">
                    {page.success ? (
                      <CheckCircle className="status-icon success" />
                    ) : (
                      <XCircle className="status-icon error" />
                    )}
                    <span className="title-text">{page.title || 'Untitled'}</span>
                  </div>
                  
                  <div className="page-details">
                    <a 
                      href={page.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="page-url"
                    >
                      <ExternalLink className="external-icon" />
                      {formatUrl(page.url)}
                    </a>
                    <span className="word-count">{page.wordCount} words</span>
                  </div>
                  
                  {page.error && (
                    <div className="page-error">{page.error}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {selectedPages.length > 0 && (
            <div className="process-section">
              <p>Selected {selectedPages.length} pages to process</p>
              <button 
                onClick={handleProcess}
                disabled={isProcessing}
                className="process-button"
              >
                {isProcessing ? (
                  <>
                    <Loader className="spinner" />
                    Processing...
                  </>
                ) : (
                  'Add to Knowledge Base'
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WebsiteScanner;
